import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { prefixUrl } from '@ecam/eventq-components';
import { firstValueFrom } from 'rxjs';

/** One live camera channel — maps to streamer `webrtc_code`. */
export interface LiveStreamChannel {
  webrtcCode: string;
  /** When present, connect via WHEP (native WebRTC) for staging proxy paths. */
  whepUrl?: string;
}

/** Runtime JSON for demo/staging live camera streams. */
export interface LiveStreamRuntimeConfig {
  /** Base streamer API (initialize-stream, ice-candidate, disconnect). */
  webRtcApi?: string;
  userType?: number;
  streams?: unknown[];
}

const PROXY_URL_PATTERN = /^https?:\/\/[^/]+\/proxy\/([^/?#]+)\/?$/i;

/** Parse a runtime config entry into a live channel. */
export function parseLiveStreamChannel(entry: unknown): LiveStreamChannel | null {
  if (typeof entry === 'string') {
    const trimmed = entry.trim();
    if (!trimmed) {
      return null;
    }
    const proxyMatch = trimmed.match(PROXY_URL_PATTERN);
    if (proxyMatch) {
      const code = proxyMatch[1];
      const base = trimmed.replace(/\/?$/, '');
      return { webrtcCode: code, whepUrl: `${base}/whep` };
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return {
        webrtcCode: trimmed,
        whepUrl: trimmed.endsWith('/whep') ? trimmed : `${trimmed.replace(/\/?$/, '')}/whep`,
      };
    }
    return { webrtcCode: trimmed };
  }

  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const record = entry as Record<string, unknown>;
  const code = typeof record['webrtcCode'] === 'string' ? record['webrtcCode'].trim() : '';
  if (!code) {
    return null;
  }

  const whepUrl =
    typeof record['whepUrl'] === 'string' && record['whepUrl'].trim()
      ? record['whepUrl'].trim()
      : undefined;

  return whepUrl ? { webrtcCode: code, whepUrl } : { webrtcCode: code };
}

export function normalizeLiveStreamChannels(entries: unknown): LiveStreamChannel[] {
  if (!Array.isArray(entries)) {
    return [];
  }

  const channels: LiveStreamChannel[] = [];
  for (const entry of entries) {
    const channel = parseLiveStreamChannel(entry);
    if (channel) {
      channels.push(channel);
    }
  }
  return channels;
}

/** Stable round-robin by camera list index. */
export function resolveLiveStreamChannel(
  channels: readonly LiveStreamChannel[],
  cameraIndex: number,
): LiveStreamChannel | null {
  if (!channels.length || !Number.isFinite(cameraIndex) || cameraIndex < 0) {
    return null;
  }
  return channels[Math.floor(cameraIndex) % channels.length] ?? null;
}

/** @deprecated Prefer {@link normalizeLiveStreamChannels}. */
export function normalizeLiveStreamUrls(entries: unknown): string[] {
  return normalizeLiveStreamChannels(entries).map((channel) => channel.webrtcCode);
}

/** @deprecated Prefer {@link resolveLiveStreamChannel}. */
export function resolveLiveStreamUrl(
  streams: readonly string[],
  cameraIndex: number,
): string | null {
  return resolveLiveStreamChannel(
    streams.map((webrtcCode) => ({ webrtcCode })),
    cameraIndex,
  )?.webrtcCode ?? null;
}

/**
 * Loads deployment-injected live stream channels from `/config/live-streams.json`.
 * Tracked example: `public/config/live-streams.example.json`.
 */
@Injectable({ providedIn: 'root' })
export class LiveStreamRuntimeConfigService {
  private readonly http = inject(HttpClient);
  private readonly channelList = signal<LiveStreamChannel[]>([]);
  private readonly apiBase = signal('');
  private readonly streamUserType = signal(1);

  readonly streams = this.channelList.asReadonly();
  readonly webRtcApi = this.apiBase.asReadonly();
  readonly userType = this.streamUserType.asReadonly();

  resolveChannelForCameraIndex(cameraIndex: number): LiveStreamChannel | null {
    return resolveLiveStreamChannel(this.channelList(), cameraIndex);
  }

  /** @deprecated Use {@link resolveChannelForCameraIndex}. */
  resolveUrlForCameraIndex(cameraIndex: number): string | null {
    return this.resolveChannelForCameraIndex(cameraIndex)?.webrtcCode ?? null;
  }

  async load(): Promise<void> {
    try {
      const body = await firstValueFrom(
        this.http.get<LiveStreamRuntimeConfig | LiveStreamChannel[] | string[]>(
          prefixUrl('/config/live-streams.json'),
        ),
      );
      const record = Array.isArray(body) ? { streams: body } : body;
      const raw = record?.streams;
      this.channelList.set(normalizeLiveStreamChannels(raw));
      this.apiBase.set(typeof record?.webRtcApi === 'string' ? record.webRtcApi.trim() : '');
      this.streamUserType.set(
        typeof record?.userType === 'number' && Number.isFinite(record.userType)
          ? record.userType
          : 1,
      );
    } catch {
      this.channelList.set([]);
      this.apiBase.set('');
      this.streamUserType.set(1);
    }
  }
}
